import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, RotateCcw, Trash2, RefreshCw } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export const AuthRecovery = () => {
  const { forceSignOut, refreshSession, validateSession } = useAuth();
  const [isRecovering, setIsRecovering] = useState(false);

  const clearBrowserData = () => {
    try {
      // Clear localStorage
      localStorage.clear();
      
      // Clear sessionStorage
      sessionStorage.clear();
      
      toast.success('Browser data cleared');
      
      // Reload page after clearing data
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error('Failed to clear browser data:', error);
      toast.error('Failed to clear browser data');
    }
  };

  const handleForceSignOut = async () => {
    setIsRecovering(true);
    try {
      await forceSignOut();
      toast.success('Successfully signed out');
      
      // Redirect to auth page after force sign out
      setTimeout(() => {
        window.location.href = '/auth';
      }, 1000);
    } catch (error) {
      console.error('Force sign out failed:', error);
      toast.error('Force sign out failed - trying browser data clear');
      
      // If force sign out fails, try clearing browser data
      clearBrowserData();
    } finally {
      setIsRecovering(false);
    }
  };

  const handleRefreshSession = async () => {
    setIsRecovering(true);
    try {
      await refreshSession();
      const isValid = await validateSession();
      
      if (isValid) {
        toast.success('Session refreshed successfully');
        window.location.reload();
      } else {
        toast.error('Session refresh failed - trying force sign out');
        await handleForceSignOut();
      }
    } catch (error) {
      console.error('Session refresh failed:', error);
      toast.error('Session refresh failed - trying force sign out');
      await handleForceSignOut();
    } finally {
      setIsRecovering(false);
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
          <AlertTriangle className="h-6 w-6 text-destructive" />
        </div>
        <CardTitle>Authentication Recovery</CardTitle>
        <p className="text-sm text-muted-foreground">
          It looks like you're experiencing authentication issues. Try these recovery options:
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <Button
          onClick={handleRefreshSession}
          disabled={isRecovering}
          className="w-full"
          variant="outline"
        >
          <RefreshCw className="mr-2 h-4 w-4" />
          {isRecovering ? 'Refreshing...' : 'Refresh Session'}
        </Button>
        
        <Button
          onClick={handleForceSignOut}
          disabled={isRecovering}
          className="w-full"
          variant="outline"
        >
          <RotateCcw className="mr-2 h-4 w-4" />
          {isRecovering ? 'Signing Out...' : 'Force Sign Out'}
        </Button>
        
        <Button
          onClick={clearBrowserData}
          disabled={isRecovering}
          className="w-full"
          variant="destructive"
        >
          <Trash2 className="mr-2 h-4 w-4" />
          Clear Browser Data
        </Button>
        
        <p className="text-xs text-muted-foreground text-center">
          If issues persist, try "Clear Browser Data" and sign back in.
        </p>
      </CardContent>
    </Card>
  );
};